<template>
    <div id="main" class="is-flex">
        <!-- Título -->
        <div class="page">
            <div class="vertical-flow">
                <div class="listing-header">
                    <div class="title-breadcrumb">
                        <h1 class="title">{{ $t('resultado-objetivo-vendas') }}</h1>
                        <nav class="breadcrumb" aria-label="breadcrumbs">
                            <ul>
                                <li>
                                    <a>{{ $t('vendas') }}</a>
                                </li>
                                <li>
                                    <a>{{ $t('relatorios') }}</a>
                                </li>
                                <li>
                                    <a href="#" aria-current="page">{{
                                        $t('resultado-objetivo-vendas')
                                    }}</a>
                                </li>
                            </ul>
                        </nav>
                    </div>
                    <div class="header-buttons">
                        <button
                            class="delete-btn"
                            type="button"
                            tag="button"
                            @click="$router.push({ name: `${abrev}-cockpit` })"
                        >
                            {{ $t('voltar') }}
                        </button>
                    </div>
                </div>

                <!-- FILTROS -->
                <div class="columns is-multiline search-filters">
                    <div class="column">
                        <vss-select
                            v-model="vendedoresSelecionados"
                            label="nome"
                            :options="vendedores"
                            title="vendedores"
                            :height="40"
                            @search="loadVendedores"
                            @input="debouncedOnSearch"
                            no-clearfix
                            searchable
                            clearable
                            multiple
                            :closeOnSelect="false"
                        >
                            <template #multiple-selected="{ selected }">
                                {{ $t('selecionado') }} ({{
                                    selected && selected.length > 0 ? selected.length : $t('todos')
                                }})
                            </template>
                        </vss-select>
                    </div>
                    <div class="column">
                        <vss-select
                            title="tipo-pedido"
                            placeholder="selecione"
                            :options="tipoPedidoList"
                            v-model="tipoPedidoSelected"
                            :loading="tipoPedidoLoading"
                            @search="carregarTipoPedido"
                            @input="debouncedOnSearch"
                            :height="40"
                            multiple
                            searchable
                            clearable
                            no-clearfix
                            :closeOnSelect="false"
                        >
                            <template #multiple-selected="{ selected }">
                                {{ $t('selecionado') }} ({{
                                    selected && selected.length > 0 ? selected.length : $t('todos')
                                }})
                            </template>
                        </vss-select>
                    </div>
                    <div class="column is-2">
                        <vss-select
                            title="ranking-por"
                            v-model="ranking"
                            :options="tiposRanking"
                            :height="40"
                            @input="loadData"
                            no-clearfix
                            ml-labels
                        />
                    </div>
                    <div class="column is-2">
                        <label for="inicio">{{ $t('inicio') }}</label>
                        <!-- 
                            O que acontece no month-names?
        
                            Se definir essas strings lá no script, os nomes dos meses não vão trocar automaticamente
                            com a língua. Então tem que definir no template mesmo.
        
                            Tudo relacionado ao dicionário que queira que troque instantaneamente com a troca de língua deve
                            estar presente no template e não no script.
                        -->
                        <!-- Não alterar b-datepicker para verified-date-picker! O formato aqui é MM/YYYY, enquanto
                        o verified-date-picker tem formato DD/MM/YYYY @70513 -->
                        <b-datepicker
                            v-model="mesIni"
                            class="dpicker-filter"
                            type="month"
                            position="is-bottom-left"
                            :month-names="[
                                $t('january'),
                                $t('february'),
                                $t('march'),
                                $t('april'),
                                $t('may'),
                                $t('june'),
                                $t('july'),
                                $t('august'),
                                $t('september'),
                                $t('october'),
                                $t('november'),
                                $t('december'),
                            ]"
                            :date-formatter="(date) => $moment(date).format('MM/YYYY')"
                            :max-date="mesFim"
                            :mobile-native="false"
                            @input="loadData"
                            icon="calendar-today"
                        />
                    </div>
                    <div class="column is-2">
                        <label for="fim">{{ $t('fim') }}</label>
                        <!-- Não alterar b-datepicker para verified-date-picker! O formato aqui é MM/YYYY, enquanto 
                        o verified-date-picker tem formato DD/MM/YYYY @70513 -->
                        <b-datepicker
                            v-model="mesFim"
                            class="dpicker-filter"
                            type="month"
                            position="is-bottom-left"
                            :month-names="[
                                $t('january'),
                                $t('february'),
                                $t('march'),
                                $t('april'),
                                $t('may'),
                                $t('june'),
                                $t('july'),
                                $t('august'),
                                $t('september'),
                                $t('october'),
                                $t('november'),
                                $t('december'),
                            ]"
                            :date-formatter="(date) => $moment(date).format('MM/YYYY')"
                            :min-date="mesIni"
                            :max-date="$moment(mesIni).add(3, 'years').toDate()"
                            :mobile-native="false"
                            @input="loadData"
                            icon="calendar-today"
                        />
                    </div>
                </div>

                <div class="data is-grow-1 is-flex">
                    <div class="registry-wrapper is-grow-1">
                        <container-loader v-if="isLoading && !$store.getters.isLoading" />
                        <empty-table-message v-else-if="isEmpty" />
                        <b-table
                            v-else
                            ref="table"
                            class="data-table has-sticky-footer"
                            id="resultados-objetivo-table"
                            height="calc(100vh - 245px)"
                            :data="displayTableData"
                            :show-detail-icon="false"
                            sticky-header
                            scrollable
                            detailed
                        >
                            <b-table-column
                                v-for="(f, i) in tableFields"
                                class="td-center"
                                :key="i"
                                :field="f.key"
                                :label="f.label ? $t(f.label) : ''"
                                :width="f.width"
                                :centered="f.centered"
                                :numeric="f.numeric"
                            >
                                <template v-slot:header>
                                    <div :class="{ 'is-clickable': f.tip }" @click="openTip(f)">
                                        {{ f.label != '' ? $t(f.label) : '' }}
                                    </div>
                                </template>
                                <template v-slot="props">
                                    <b-button
                                        v-if="f.key == 'expand'"
                                        type="is-primary"
                                        icon-left="poll"
                                        :loading="props.row.loadingPedidos"
                                        :disabled="props.row.totalVendas <= 0"
                                        @click="abrirPedidos(props.row)"
                                        v-tippy
                                        :title="
                                            props.row.totalVendas > 0
                                                ? $t('abrir-pedidos')
                                                : $t('pedido-nodata')
                                        "
                                    />

                                    <div v-if="f.key == 'usuario'">
                                        {{ props.row.usuario.nome }}
                                    </div>

                                    <!-- % do objetivo -->
                                    <div
                                        v-else-if="
                                            f.key == 'porcObjetivo' && props.row.objetivoVendas
                                        "
                                        class="is-flex is-flex-hbetween is-flex-vcenter"
                                    >
                                        <span>{{ props.row.porcObjetivo | decimal(2) }}%</span>
                                        <span
                                            class="is-size-7 has-text-weight-bold"
                                            :style="{
                                                color:
                                                    props.row.porcDiferenca != 0
                                                        ? props.row.porcDiferenca > 0
                                                            ? 'var(--green-alert)'
                                                            : 'var(--red-alert)'
                                                        : '',
                                            }"
                                        >
                                            <b-icon
                                                v-if="props.row.porcDiferenca != 0"
                                                :icon="
                                                    props.row.porcDiferenca > 0
                                                        ? 'arrow-up'
                                                        : 'arrow-down'
                                                "
                                                style="width: 12px"
                                                size="is-small"
                                            />
                                            {{ Math.abs(props.row.porcDiferenca) | decimal(2) }}%
                                        </span>
                                    </div>
                                    <div
                                        v-else-if="
                                            f.key == 'porcObjetivo' && !props.row.objetivoVendas
                                        "
                                    >
                                        —
                                    </div>

                                    <!-- previsão de vendas -->
                                    <div
                                        v-else-if="f.key == 'previsaoVendas'"
                                        :style="{
                                            color:
                                                props.row.objetivoVendas &&
                                                props.row.porcDiferenca != 0
                                                    ? props.row.porcDiferenca > 0
                                                        ? 'var(--green-alert)'
                                                        : 'var(--red-alert)'
                                                    : '',
                                        }"
                                    >
                                        {{ props.row[f.key] | decimal(null, true) }}
                                    </div>

                                    <div
                                        v-else-if="
                                            [
                                                'totalVendas',
                                                'objetivoVendas',
                                                'ticketMedio',
                                                'ticketMedioIdeal',
                                            ].includes(f.key)
                                        "
                                    >
                                        {{ props.row[f.key] | decimal(null, true) }}
                                    </div>
                                    <div v-else>
                                        {{ props.row[f.key] }}
                                    </div>
                                </template>
                            </b-table-column>

                            <!-- Tabela interna de pedidos -->
                            <template slot="detail" slot-scope="props">
                                <div
                                    class="has-text-centered has-text-weight-bold"
                                    v-if="props.row.pedidos.length == 0"
                                >
                                    {{ $t('pedido-nodata') }}
                                </div>
                                <b-table v-else :data="props.row.pedidos">
                                    <b-table-column
                                        v-for="(pf, ip) in pedidoTableFields"
                                        :key="ip"
                                        :field="pf.key"
                                        :label="pf.label != '' ? $t(pf.label) : ''"
                                        :width="pf.width"
                                        :centered="pf.centered"
                                        :numeric="pf.numeric"
                                        v-slot="props"
                                    >
                                        <div v-if="pf.key == 'nome' || pf.key == 'rsocial'">
                                            {{ props.row.cliente[pf.key] }}
                                        </div>
                                        <div v-else-if="pf.key == 'valortotal'">
                                            {{ props.row.valortotal | decimal(null, true) }}
                                        </div>
                                        <div v-else-if="pf.key == 'status'">
                                            <span
                                                :class="{
                                                    'tag is-warning':
                                                        props.row.registro_enviado == 'E',
                                                    'tag is-success':
                                                        props.row.registro_enviado == 'S',
                                                    'tag is-danger':
                                                        props.row.registro_enviado == 'N',
                                                }"
                                            >
                                                {{
                                                    props.row.registro_enviado == 'E'
                                                        ? $t('processando')
                                                        : props.row.registro_enviado == 'S'
                                                        ? $t('transmitido')
                                                        : $t('nao-enviado')
                                                }}
                                            </span>
                                        </div>
                                        <div v-else>
                                            {{ props.row[pf.key] }}
                                        </div>
                                    </b-table-column>
                                </b-table>
                            </template>

                            <!-- RODAPÉ DA TABELA -->
                            <template slot="footer">
                                <th scope="col" class="has-text-centered">
                                    <span class="is-hidden-tablet">{{ $t('total') }}</span>
                                </th>
                                <th scope="col" class="has-text-centered-mobile">
                                    {{ totais.usuario }} {{ $t('vendedores') }}
                                </th>
                                <th
                                    scope="col"
                                    class="has-text-centered is-flex-mobile is-flex-hbetween"
                                >
                                    <span class="is-hidden-tablet">{{ $t('porc-objetivo') }}</span>
                                    <div
                                        class="is-flex-desktop is-flex-hbetween is-flex-vcenter"
                                        v-if="
                                            totais.porcObjetivo != undefined &&
                                            totais.porcDiferenca != undefined
                                        "
                                    >
                                        {{ totais.porcObjetivo | decimal(2) }}%
                                        <span
                                            class="is-size-7 has-text-weight-bold"
                                            :style="{
                                                color:
                                                    totais.porcDiferenca != 0
                                                        ? totais.porcDiferenca > 0
                                                            ? 'var(--green-alert)'
                                                            : 'var(--red-alert)'
                                                        : '',
                                            }"
                                        >
                                            <b-icon
                                                v-if="totais.porcDiferenca != 100"
                                                :icon="
                                                    totais.porcDiferenca <= 0
                                                        ? 'arrow-down'
                                                        : 'arrow-up'
                                                "
                                                style="width: 12px"
                                                size="is-small"
                                            />
                                            {{ Math.abs(totais.porcDiferenca) | decimal(2) }}%
                                        </span>
                                    </div>
                                    <span v-else> – </span>
                                </th>
                                <th
                                    scope="col"
                                    class="has-text-right is-flex-mobile is-flex-hbetween"
                                >
                                    <span class="is-hidden-tablet">{{
                                        $t('total-de-vendas')
                                    }}</span>
                                    {{ totais.vendas | decimal(null, true) }}
                                </th>
                                <th
                                    scope="col"
                                    class="has-text-right is-flex-mobile is-flex-hbetween"
                                >
                                    <span class="is-hidden-tablet">{{
                                        $t('objetivo-vendas')
                                    }}</span>
                                    {{ totais.objetivo | decimal(null, true) }}
                                </th>
                                <th
                                    scope="col"
                                    class="has-text-right is-flex-mobile is-flex-hbetween"
                                    :style="{
                                        color:
                                            totais.previsao >= totais.objetivo
                                                ? 'var(--green-alert)'
                                                : 'var(--red-alert)',
                                    }"
                                >
                                    <span class="is-hidden-tablet">{{
                                        $t('previsao-vendas')
                                    }}</span>
                                    {{ totais.previsao | decimal(null, true) }}
                                </th>
                                <th
                                    scope="col"
                                    class="has-text-right is-flex-mobile is-flex-hbetween"
                                >
                                    <span class="is-hidden-tablet">{{ $t('ticket-medio') }}</span>
                                    {{ totais.ticketMedio | decimal(null, true) }}
                                </th>
                                <th
                                    scope="col"
                                    class="has-text-right is-flex-mobile is-flex-hbetween"
                                >
                                    <span class="is-hidden-tablet">{{
                                        $t('ticket-medio-esperado')
                                    }}</span>
                                    {{ totais.ticketMedioIdeal | decimal(null, true) }}
                                </th>
                                <th scope="col"></th>
                            </template>
                        </b-table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ContainerLoader from '@/components/content/loader/ContainerLoader';
import ResultadoObjetivoVendasService from '@/services/ResultadoObjetivoVendasService';
import ColaboradorService from '@/services/ColaboradorService';
import TipoPedidoService from '@/services/vendas/TipoPedidoService.js';

export default {
    components: {
        ContainerLoader,
    },
    data() {
        return {
            service: new ResultadoObjetivoVendasService(this.$http, this.$router),
            colaboradorService: new ColaboradorService(this.$http, this.$router),
            tipoPedidoService: new TipoPedidoService(this.$http, this.$router),
            isLoading: false,
            isEmpty: false,
            searchQuery: '',
            /**
             * Filtros padrão:
             * - mês início e fim atual
             * - ranking por % de objetivo
             */
            mesIni: this.$moment().date(1).toDate(),
            mesFim: this.$moment().date(1).toDate(),
            ranking: 'PORCENTAGEM_OBJETIVO',
            tiposRanking: ['PORCENTAGEM_OBJETIVO', 'TOTAL_VENDAS'],
            /**
             * Dados para as tabelas
             */
            tableFields: [
                { key: 'ranking', label: 'ranking', width: 100, centered: true },
                { key: 'usuario', label: 'vendedor', suggestedWidth: 300 },
                { key: 'porcObjetivo', label: 'porc-objetivo', width: 200, centered: true },
                { key: 'totalVendas', label: 'total-de-vendas', width: 200, numeric: true },
                { key: 'objetivoVendas', label: 'objetivo-vendas', width: 200, numeric: true },
                { key: 'previsaoVendas', label: 'previsao-vendas', width: 200, numeric: true },
                { key: 'ticketMedio', label: 'ticket-medio', width: 150, numeric: true, tip: true },
                {
                    key: 'ticketMedioIdeal',
                    label: 'ticket-medio-esperado',
                    width: 210,
                    numeric: true,
                },
                { key: 'expand', label: '', width: 50, centered: true },
            ],
            tableData: [],
            pedidoTableFields: [
                { key: 'idpedidoerp', label: 'codigo', width: 100, centered: true },
                { key: 'nome', label: 'nome' },
                { key: 'rsocial', label: 'razao-social' },
                { key: 'tipopedido', label: 'tipo-pedido' },
                { key: 'valortotal', label: 'valor-total', numeric: true },
                { key: 'status', label: 'status', width: 125, centered: true },
            ],
            /**
             * Combo de vendedor
             */
            vendedoresLoading: false,
            vendedoresSelecionados: [],
            vendedores: [],
            tipoPedidoList: [],
            tipoPedidoSelected: [],
            tipoPedidoLoading: false,
            tableWidth: 0,
            permissao: {},
            // @84575 - Modificado totais de computed para data
            totais: {},
            // @84575 - Adicionado método de search debounced para seleção de vendedores
            debouncedOnSearch: this.debounce(this.onSearch, 500),
            abrev: this.$route.meta.contexto.slice(0, 3),
            isAgro: this.$route.meta.contexto == 'agro',
            search: null,
            resizeEvent: null,
        };
    },
    created() {
        /**
         * Função de pesquisa com this.debounce
         */
        this.search = this.debounce(function (value) {
            this.searchQuery = value;
        }, 200);
        this.permissao = this.hasPermissionAccess(
            'vendas',
            'relatorio-analitico',
            'relatorio-vendas-resultado-objetivo-vendas'
        );
        /**
         * Evento para redimensionar a tabela
         */
        this.resizeEvent = this.debounce(function () {
            let tabela = document.querySelector('#resultados-objetivo-table');

            /*  Pode ser que o elemento não exista - ex. carregando - então é bom verificar se ele existe
                    antes de manipular ele. */
            if (tabela) {
                let currentWidth = tabela.offsetWidth;

                if (window.innerWidth > 768) {
                    document.querySelector('#resultados-objetivo-table table.table').style.width =
                        this.tableWidth > currentWidth + 50 ? `${this.tableWidth}px` : '';
                } else {
                    document.querySelector('#resultados-objetivo-table table.table').style.width =
                        '';
                }
            }
        }, 200);

        if (this.isAgro) this.abrev = this.$route.meta.contexto.slice(0, 4);

        !this.permissao.read &&
            this.$router.push({
                name: 'acesso-nao-permitido',
                params: { tela: 'resultado-objetivo-vendas' },
            });

        /**
         * Este parâmetro vem pelo router quando utilizado o botão "Detalhar Dados"
         * no cockpit de evolução de vendas.
         *
         * Para satisfazer o requisito 10 do chamado @68737
         */
        if (this.$route.params.mesSelecionado) {
            let { mes, ano } = this.$route.params.mesSelecionado;

            this.mesIni = this.$moment([ano, mes - 1, 1]).toDate();
            this.mesFim = this.$moment([ano, mes - 1, 1]).toDate();
        }

        if (this.$route.params.vendedores) {
            this.vendedoresSelecionados = this.$route.params.vendedores;
        }

        if (this.$route.params.tiposPedido) {
            this.tipoPedidoSelected = this.$route.params.tiposPedido;
        }

        this.loadData();
        this.loadVendedores('');
        // @84575 - Foi necessário chamar pois ocorre problema na primeira tentativa, caso seja feito apenas um input de vendedor
        this.debouncedOnSearch();

        window.addEventListener('resize', this.resizeEvent);
        this.tableWidth = this.tableFields.reduce(
            (a, c) => a + (c.width || c.suggestedWidth || 150),
            0
        );
    },
    destroyed() {
        window.removeEventListener('resize', this.resizeEvent);
    },
    computed: {
        /**
         * Cria um objeto com todos os totais calculados para fácil acesso no
         * footer da tabela.
         */

        /**
         * Dados da tabela após a filtragem dos vendedores.
         * Vendedores são filtrados pelo front pois são carregados sem paginação.
         */
        displayTableData() {
            return this.tableData;
        },
    },
    methods: {
        /**
         * Carregar dados novos, chamado na mudança dos filtros de ranking,
         * mês de início e fim. Os vendedores são filtrados no front.
         */
        loadData() {
            let filtros = {
                datainicio: this.$moment(this.mesIni).format('YYYY-MM-DD'),
                datafim: this.$moment(this.mesFim).format('YYYY-MM-DD'),
                ranking: this.ranking,
                tiposPedido: this.tipoPedidoSelected.map((e) => e.id),
            };

            this.isLoading = true;

            this.service
                .getAll(filtros)
                .then((r) => {
                    this.tableData = r;
                    this.isEmpty = this.tableData.length <= 0;
                })
                .catch((e) => {
                    this.isEmpty = true;
                    console.error('Erro ao carregar resultados.', e);
                })
                .finally((_) => {
                    this.isLoading = false;

                    this.$nextTick(function () {
                        this.resizeEvent();
                    });
                    // @84575 - Após fazer nova consulta ao banco, recalcula os totais
                    this.calculaTotal();
                });
        },
        /**
         * Carregar os vendedores no combo
         */
        loadVendedores(query) {
            this.vendedoresLoading = true;
            this.colaboradorService
                .findList(query)
                .then((r) => {
                    this.vendedores = r.content;
                })
                .catch((e) => {
                    console.error(`Erro ao pesquisar vendedores (${query}).`, e);
                })
                .finally((_) => {
                    this.vendedoresLoading = false;
                });
        },
        /**
         * Para abrir os pedidos de um usuário específico.
         *
         * Algumas flags são inseridas após o carregamento para
         * detectar se precisa chamar o servidor para carregar eles.
         *
         * Pois uma vez carregado os pedidos, não é necessário carregá-los
         * novamente.
         *
         * Quando o filtro é modificado, veem novos dados e os mesmos
         * não possuem as flags, logo não é necessário se preocupar em
         * resetar as flags na mudança de filtro.
         */
        abrirPedidos(row) {
            if (row.pedidosLoaded === true) {
                this.toggleRow(row);
            } else {
                /**
                 * O mesmo que fazer row.loadingPedidos = false
                 * Necessário para manter reatividade e exibir
                 * corretamente o loader no botão
                 */
                this.$set(row, 'loadingPedidos', true);

                let filtros = {
                    datainicio: this.$moment(this.mesIni).format('YYYY-MM-DD'),
                    datafim: this.$moment(this.mesFim).format('YYYY-MM-DD'),
                    tiposPedido: this.tipoPedidoSelected.map((e) => e.id),
                };

                setTimeout(() => {
                    this.service
                        .getPedidosByUsuario(row.usuario.id, filtros)
                        .then((r) => {
                            row.pedidos = r;
                            row.pedidosLoaded = true;
                            this.toggleRow(row);
                        })
                        .catch((e) => {
                            console.error(
                                `Erro ao carregar pedidos do vendedor ${row.usuario.id}`,
                                e
                            );
                        })
                        .finally((_) => {
                            this.$set(row, 'loadingPedidos', false);
                        });
                }, 1000);
            }
        },
        /**
         * Abre ou fecha os detalhes de uma linha específica da tabela
         */
        toggleRow(row) {
            this.$refs.table.toggleDetails(row);
        },
        /**
         * Calcula a quantidade de dias úteis entre o início do mês inicial
         * e o fim do mês final.
         *
         * Dias úteis considerando somente segunda a sexta.
         */
        getDiasUteisEntreInicioFim() {
            let qtd = 0;
            let mIni = this.$moment(this.mesIni).startOf('month');
            let mFim = this.$moment(this.mesFim).endOf('month');

            while (mIni.isSameOrBefore(mFim, 'day')) {
                if (mIni.isoWeekday() < 6) {
                    qtd++;
                }

                mIni.add(1, 'day');
            }

            return qtd;
        },
        /**
         * Mesmo que o método acima porém conta somente até a data atual
         */
        getDiasUteisEntreInicioFimOuAteHoje() {
            let qtd = 0;
            let mIni = this.$moment(this.mesIni).startOf('month');
            let mFim = this.$moment(this.mesFim).endOf('month');
            let now = this.$moment();

            while (mIni.isSameOrBefore(mFim, 'day') && mIni.isSameOrBefore(now, 'day')) {
                if (mIni.isoWeekday() < 6) {
                    qtd++;
                }

                mIni.add(1, 'day');
            }

            return qtd;
        },
        openTip(column) {
            let tipText;

            switch (column.key) {
                case 'ticketMedio':
                    tipText = this.$t('ticket-medio-tip', { n: this.totais.diasUteisAteHoje });
                    break;
            }

            if (tipText) {
                this.$root.alert({ message: tipText });
            }
        },
        // @84575 - Método criado a partir da computed propertie que existia, para atualizar os totais
        calculaTotal() {
            let idVendedoresSelecionados = this.vendedoresSelecionados.map((v) => v.id);

            /* Se existir algo no filtro, faz a filtragem do array existente */
            if (idVendedoresSelecionados.length > 0) {
                this.tableData = this.tableData.filter((item) =>
                    idVendedoresSelecionados.includes(item.usuario.id)
                );
            }
            let totais = {};

            let diasUteisPeriodo = this.getDiasUteisEntreInicioFim();
            let diasUteisAteHoje = this.getDiasUteisEntreInicioFimOuAteHoje();

            totais.usuario = this.displayTableData.length;
            totais.vendas = this.displayTableData.reduce((a, c) => a + (c.totalVendas || 0), 0);
            totais.objetivo = this.displayTableData.reduce(
                (a, c) => a + (c.objetivoVendas || 0),
                0
            );
            totais.previsao = this.displayTableData.reduce(
                (a, c) => a + (c.previsaoVendas || 0),
                0
            );

            if (totais.objetivo > 0) {
                totais.porcObjetivo = (totais.vendas / totais.objetivo) * 100;
                totais.porcDiferenca = (totais.previsao / totais.objetivo - 1) * 100;
            }

            // @84581 - Padronizado o somatório dos totais
            totais.ticketMedio = this.displayTableData.reduce(
                (a, c) => a + (c.ticketMedio || 0),
                0
            );
            totais.ticketMedioIdeal = this.displayTableData.reduce(
                (a, c) => a + (c.ticketMedioIdeal || 0),
                0
            );

            totais.diasUteisPeriodo = diasUteisPeriodo;
            totais.diasUteisAteHoje = diasUteisAteHoje;

            this.totais = totais;
        },
        onSearch() {
            this.loadData();
        },
        carregarTipoPedido(query = null) {
            this.tipoPedidoLoading = true;
            query = this.isNull(query) ? '.*' : this.removeToSearch(query);

            this.tipoPedidoService
                .findAllCombo(query)
                .then(this.mapTipoPedidoList)
                .then((value) => {
                    if (value != null) {
                        this.tipoPedidoList = value;
                    }
                })
                .catch((e) => {
                    this.$root.showMessage('error', 'msg-erro-load', true);
                })
                .finally((_) => {
                    this.tipoPedidoLoading = false;
                });
        },
        mapTipoPedidoList(response) {
            return response.content.map((m) => {
                return {
                    id: m.id,
                    label: m.descricao,
                };
            });
        },
    },
};
</script>

<style lang="scss" scoped>
#main {
    padding: 0 15px 15px 20px;
    min-height: 100%;
    flex-flow: column;

    .data {
        position: relative;
        background-color: $sovis-white;
        box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
        border: 1px solid rgba(0, 0, 0, 0.1);
        border-radius: 2px;
        padding: 1rem;

        .registry-wrapper {
            overflow-x: auto;
        }
    }
}
</style>
